import posthog from 'posthog-js';
import { trackPageView } from './pageTracking';

// Self-hosted PostHog instance (EU). The project key is injected by the
// `mixpanel/_mixpanel` partial as `window.posthogKey`.
const HOST = 'https://analytics.hiiker.com';

// Idempotency guard: this module is loaded both by the `mixpanel/_mixpanel`
// partial entrypoint (so analytics + the window.mixpanel shim exist on EVERY
// page, like the old CDN stub did) and by `utils/analytics/analytics.ts` (for
// layout-less pages such as pdf/static_maps that don't render the partial).
// The guard ensures init, the shim, and the page-view listener run only once.
if (!window.__hiikerAnalyticsInitialised) {
  window.__hiikerAnalyticsInitialised = true;

  if (window.posthogKey) {
    posthog.init(window.posthogKey, {
      api_host: HOST,
      capture_pageview: false, // we send "Page View" manually (matches imported history)
      capture_pageleave: false,
      autocapture: false, // keep events 1:1 with the imported Mixpanel data
      disable_session_recording: true,
      // The following default to ON and emit their own auto events ($web_vitals,
      // survey shown/sent, dead-click). Disabled to keep "manual events only" and
      // avoid inflating volume on the self-hosted instance.
      capture_performance: false, // no $web_vitals / performance events
      disable_surveys: true,
      capture_dead_clicks: false,
      persistence: 'localStorage',
    });

    // distinct_id == Firebase UID, so live events line up with the imported history.
    if (window.user?.firebase_user_id) {
      posthog.identify(window.user.firebase_user_id);
    }
  }

  window.posthog = posthog;

  // Backwards-compatible facade: existing call sites across the app use
  // `window.mixpanel.*`. These now route to PostHog, preserving event names and
  // the Firebase-UID identity. (Defined unconditionally so unguarded call sites
  // never throw, even if the key is missing.)
  window.mixpanel = {
    init: () => {},
    identify: (id: string) => posthog.identify(id),
    track: (event: string, properties?: Record<string, any>) => posthog.capture(event, properties),
    register: (properties: Record<string, any>) => posthog.register(properties),
    reset: () => posthog.reset(),
    people: {
      set: (properties: Record<string, any>) => posthog.setPersonProperties(properties),
    },
  };

  // Manual page-view tracking on initial load and Turbo navigations.
  trackPageView();
  document.addEventListener('turbo:load', () => {
    trackPageView();
  });
}
